/**
 * 배포된 AI 화면 뷰어.
 * TemplateRep1(public/templates/front/TemplateRep1.txt)과 동일한 구조(SearchPanel + SnAgGrid)로,
 * 배포 시 sys_formctrl/sys_formgridcolumn에 저장된 메타데이터를 읽어 실제 컴포넌트로 그린다.
 *
 * - cd_menu는 현재 탭 메뉴가 아니라 "배포된 cd_menu(이슈번호)"로 조회한다.
 * - getInitForm은 cd_firm/cd_module/cd_user로도 필터한다. 정상적으로는 로그인 사용자
 *   컨텍스트로 찾아야 하지만, 백엔드 배포가 아직 고정값(cd_firm='1000', cd_user='admin')을
 *   스탬프하는 환경이 남아 있어 1차 조회가 비면 그 값으로 한 번 더 시도한다.
 * - 조회 쿼리(sys_query의 `${cd_menu소문자}_01`)가 아직 없을 수 있으므로 조회 실패 시
 *   에러 대신 빈 그리드 + 안내문으로 처리한다. 단 통신/서버 예외는 원인을 그대로 보여준다.
 * - 편집기와 마찬가지로 그리드가 여러 개(헤더/내역)면 모두 세로로 쌓아 렌더한다.
 */
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import SearchIcon from "@mui/icons-material/Search";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SearchPanel from "@widgets/SearchPanel";
import SnAgGrid from "@shared/components/Grid/SnGrid";
import { fnGetDataSet } from "@shared/api/restApi";
import { getInitFormData } from "@shared/utils/CommonUtils";
import { global } from "@shared/libs/Global";
import { ColumnConfig } from "@shared/components/Grid/types";
import { menuInfo } from "@shared/type/CommonType";
import { C } from "@shared/libs/designTokens";
import { describeError } from "../errorText";

/**
 * 구버전 백엔드 deploy가 sys_formctrl/sys_formgridcolumn에 스탬프하던 고정값.
 * 로그인 사용자 컨텍스트로 못 찾았을 때만 폴백으로 쓴다.
 * (백엔드가 로그인 사용자로 스탬프하도록 바뀌면 이 상수와 폴백 조회를 제거할 것)
 */
const LEGACY_DEPLOY_STAMP = { cd_firm: "1000", cd_user: "admin" };

/**
 * 배포화면은 페이지 이동을 하지 않는다. 소스 화면(예: SN_SAL_SAO_REG_001)에서 복제된
 * drilldown 플래그(yn_drilldown="Y")를 "N"으로 낮춰, 공용 SnGrid의 셀 더블클릭
 * 네비게이션(window.onActivate 호출, SnGrid.tsx의 handleonCellDoubleClicked)을 원천 차단한다.
 * yn_drilldown은 ColumnConfig 타입에 없는 런타임 전용 필드라 any로 접근한다.
 */
function stripDrilldown(columns: ColumnConfig[]): ColumnConfig[] {
  return (columns ?? []).map((col: any) =>
    col?.yn_drilldown === "Y" ? { ...col, yn_drilldown: "N" } : col
  );
}

/** cd_grid 그룹 → 표시 순서대로 정렬된 [cdGrid, columns] 목록 (sheet%는 제외) */
function toGridList(gridGroups: Record<string, any[]>): Array<[string, ColumnConfig[]]> {
  return Object.entries(gridGroups ?? {})
    .filter(([key, cols]) => key && !key.toLowerCase().startsWith("sheet") && cols?.length)
    .sort(([a], [b]) => {
      const parse = (v: string) => {
        const m = v.match(/^(.*?)(\d+)$/);
        return m ? { head: m[1], no: Number(m[2]) } : { head: v, no: Number.MAX_SAFE_INTEGER };
      };
      const x = parse(a);
      const y = parse(b);
      return x.head.localeCompare(y.head) || x.no - y.no;
    }) as Array<[string, ColumnConfig[]]>;
}

interface Props {
  /** 배포된 cd_menu (= 이슈번호) */
  cdMenu: string;
  /** 배포된 화면명 */
  nmMenu?: string;
  /** 현재 탭(메뉴디자이너)의 Menuprops — cd_module 등 참조용 */
  Menuprops: menuInfo;
  /** 편집기로 돌아가기 */
  onBack?: () => void;
}

const DeployedScreenViewer = forwardRef(
  ({ cdMenu, nmMenu, Menuprops, onBack }: Props, ref) => {
    const searchRef = useRef<any>(null);

    const [formData, setFormData] = useState<any>();
    const [formControlData, setFormControlData] = useState<any[]>([]);
    /** 그리드 목록 — [0]=헤더, [1]=내역 … */
    const [grids, setGrids] = useState<Array<[string, ColumnConfig[]]>>([]);
    const [dataSources, setDataSources] = useState<any[][]>([]);
    const [notice, setNotice] = useState<string | null>(null);
    const [loadingMeta, setLoadingMeta] = useState(true);

    // SearchPanel/SnAgGrid에 넘길 메뉴 정보: 배포된 cd_menu 기준
    const viewerMenuInfo: menuInfo = {
      cd_menu: cdMenu,
      nm_menu: nmMenu ?? cdMenu,
      cd_module: Menuprops?.cd_module,
    };

    const queryId = cdMenu.toLowerCase() + "_01";
    // 내역(grid2) 재조회 쿼리: 헤더행 클릭 시 클릭한 행 값으로 grid2만 다시 채운다. (sys_query에 존재)
    const detailQueryId = cdMenu.toLowerCase() + "_02";

    // 배포된 메타데이터(sys_formctrl/sys_formgridcolumn) 로드
    useEffect(() => {
      let cancelled = false;
      const cdModule = Menuprops?.cd_module;

      const load = async (stamp: { cd_firm?: string; cd_user?: string }) =>
        getInitFormData({ cd_menu: cdMenu, cd_module: cdModule, ...stamp });

      const fetchform = async () => {
        setLoadingMeta(true);
        setNotice(null);
        try {
          const loginInfo: any = global.getState().userInfo ?? {};
          // 1차: 로그인 사용자 컨텍스트 / 2차: 구버전 배포 스탬프
          let rtn: any = await load({
            cd_firm: loginInfo.cd_firm,
            cd_user: loginInfo.cd_user,
          });
          let usedLegacy = false;
          if (!rtn?.formControlData?.length && !Object.keys(rtn?.grid ?? {}).length) {
            rtn = await load(LEGACY_DEPLOY_STAMP);
            usedLegacy = true;
          }
          if (cancelled) return;

          const controls = rtn?.formControlData ?? [];
          // 배포화면은 drilldown 네비게이션을 하지 않으므로 컬럼 메타에서 플래그를 제거한다.
          const gridList = toGridList(rtn?.grid ?? {}).map(
            ([cd, cols]) => [cd, stripDrilldown(cols)] as [string, ColumnConfig[]]
          );

          setFormData(rtn?.formData);
          setFormControlData(controls);
          setGrids(gridList);
          setDataSources(gridList.map(() => []));

          if (!controls.length && !gridList.length) {
            setNotice(
              `배포된 화면 메타데이터를 찾을 수 없습니다. (cd_menu=${cdMenu}, cd_firm=${
                loginInfo.cd_firm ?? "-"
              } / ${LEGACY_DEPLOY_STAMP.cd_firm} 모두 조회)`
            );
          } else if (usedLegacy) {
            setNotice(
              `구버전 배포 스탬프(cd_firm=${LEGACY_DEPLOY_STAMP.cd_firm}, cd_user=${LEGACY_DEPLOY_STAMP.cd_user})로 조회했습니다.`
            );
          }
        } catch (error) {
          if (cancelled) return;
          setNotice(`배포된 화면 메타데이터 조회 중 오류가 발생했습니다. ${describeError(error)}`);
        } finally {
          if (!cancelled) setLoadingMeta(false);
        }
      };
      fetchform();
      return () => {
        cancelled = true;
      };
    }, [cdMenu, Menuprops?.cd_module]);

    const onSearch = async () => {
      try {
        const searchData = searchRef.current?.getFormdata?.() ?? {};
        const data: any = await fnGetDataSet({ id: queryId, params: searchData });
        // 백엔드가 오류 응답(if_status='E')을 준 경우 fnGetDataSet이 배열이 아닌 값을 반환
        if (!data || data.status === "cancelled" || !Array.isArray(data)) {
          setDataSources(grids.map(() => []));
          setNotice(`조회 쿼리가 아직 준비되지 않았습니다. (sys_query: ${queryId})`);
          return;
        }
        setNotice(null);
        // 결과셋 i번째를 그리드 i번째에 바인딩
        setDataSources(grids.map((_, i) => data[i] ?? []));
      } catch (error) {
        // 여기까지 온 건 통신/세션/서버 예외다. "쿼리 미준비"로 뭉개면 원인을 못 찾는다.
        setDataSources(grids.map(() => []));
        setNotice(`조회 중 오류가 발생했습니다. ${describeError(error)} (sys_query: ${queryId})`);
      }
    };

    /**
     * grid1(헤더) 행 클릭 → 선택 행 값으로 grid2(내역)만 서버에서 재조회해 교체한다.
     * 헤더 PK 컬럼은 소스 화면마다 다르므로(수주=no_so, 입출고=no_io, 제조=no_mo …)
     * 특정 컬럼을 가정하지 않고 클릭한 행 전체를 파라미터로 넘겨, _02 쿼리가 참조하는
     * named param만 골라 바인딩하도록 한다. (no_so를 하드코딩하면 비(非)수주 소스에서
     * 조회조건이 비어 내역 갱신이 조용히 죽는다)
     * (더블클릭 네비게이션은 stripDrilldown으로 이미 비활성 — 여기선 단일클릭만 처리)
     * 페이지 이동 없이 화면 안에서 내역만 갱신하는 것이 이 화면의 요구 동작이다.
     */
    const onGrid1RowClicked = async (event: any) => {
      // 헤더+내역 2개 이상일 때만 동작 (내역 그리드가 없으면 할 일 없음)
      if (grids.length < 2) return;
      const row = event?.data;
      if (!row) return;
      try {
        const cd_firm = global.getState().userInfo?.cd_firm;
        const data: any = await fnGetDataSet({
          id: detailQueryId,
          // 클릭한 행 전체를 넘기고 로그인 cd_firm으로 덮는다. _02 쿼리는 필요한 키만 쓴다.
          params: { ...row, cd_firm },
        });
        // 오류/취소 응답(배열 아님)이면 기존 내역을 유지한다.
        if (!Array.isArray(data)) return;
        setNotice(null);
        // 내역 그리드(index 1)만 교체 — _02는 내역 단일 결과셋이므로 data[0]
        setDataSources((prev) => {
          const next = [...prev];
          next[1] = data[0] ?? [];
          return next;
        });
      } catch (error) {
        setNotice(
          `내역 조회 중 오류가 발생했습니다. ${describeError(error)} (sys_query: ${detailQueryId})`
        );
      }
    };

    useImperativeHandle(ref, () => ({
      onSearch,
    }));

    return (
      <div className="h-full flex flex-col" style={{ minHeight: 0 }}>
        {/* 뷰어 헤더: 뒤로가기 + 배포 화면 정보 + 조회 */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "8px 12px",
            background: C.panel,
            border: `1px solid ${C.border}`,
            borderRadius: 10,
            marginBottom: 8,
          }}
        >
          {onBack && (
            <button
              onClick={onBack}
              className="snd-icon-btn snd-hover"
              style={backBtn}
              aria-label="편집기로 돌아가기"
            >
              <ArrowBackIcon sx={{ fontSize: 15 }} /> 편집기
            </button>
          )}
          <span
            style={{
              fontSize: 10.5,
              fontWeight: 700,
              color: C.blue,
              background: C.blueSoft,
              border: `1px solid ${C.blueBorder}`,
              borderRadius: 6,
              padding: "2px 7px",
            }}
          >
            배포 미리보기
          </span>
          <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.2, minWidth: 0 }}>
            <span style={{ fontWeight: 800, fontSize: 13.5, color: C.text }}>
              {nmMenu ?? cdMenu}
            </span>
            <span style={{ fontSize: 10.5, color: C.grey }}>{cdMenu}</span>
          </div>
          <div style={{ flex: 1 }} />
          <button onClick={onSearch} className="snd-hover" style={searchBtn}>
            <SearchIcon sx={{ fontSize: 15 }} /> 조회
          </button>
        </div>

        {/* 안내 배너 (메타데이터 없음 / 조회 쿼리 미준비 / 오류) */}
        {notice && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "7px 12px",
              marginBottom: 8,
              fontSize: 12,
              fontWeight: 600,
              color: "#b26a00",
              background: "rgba(245,179,1,0.12)",
              border: "1px solid rgba(245,179,1,0.35)",
              borderRadius: 8,
            }}
          >
            <InfoOutlinedIcon sx={{ fontSize: 15 }} /> {notice}
          </div>
        )}

        {/* TemplateRep1과 동일한 본문: SearchPanel + SnAgGrid(N개) */}
        {loadingMeta ? (
          <div style={{ padding: 24, fontSize: 12.5, color: C.grey }}>
            배포된 화면 메타데이터를 불러오는 중...
          </div>
        ) : (
          <>
            <div className="mb-1">
              <SearchPanel
                ref={searchRef}
                data={formData}
                controls={formControlData}
              ></SearchPanel>
            </div>
            <div className="flex-1 flex flex-col" style={{ minHeight: 0, gap: 4 }}>
              {grids.map(([cdGrid, columns], i) => (
                <div key={cdGrid} className="flex-1" style={{ minHeight: 0 }}>
                  <SnAgGrid
                    menuInfo={viewerMenuInfo}
                    enableAddRow={true}
                    columnConfig={columns}
                    rowData={dataSources[i] ?? []}
                    enableExternalChart={false}
                    isEditable={false}
                    rowSelection="multiple"
                    // 헤더(grid1) 행 클릭 시 내역(grid2)만 no_so로 재조회
                    onRowClicked={i === 0 ? onGrid1RowClicked : undefined}
                  />
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    );
  }
);

const backBtn: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  height: 28,
  padding: "0 10px",
  borderRadius: 17,
  cursor: "pointer",
  fontWeight: 700,
  fontSize: 12,
  border: `1px solid ${C.border}`,
  background: "#fff",
  color: C.greyText,
};

const searchBtn: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  height: 28,
  padding: "0 12px",
  borderRadius: 17,
  cursor: "pointer",
  fontWeight: 700,
  fontSize: 12,
  border: `1px solid ${C.blue}`,
  background: C.blue,
  color: "#fff",
};

export default DeployedScreenViewer;
